import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export const useButtonVisibility = () => {
  const [buttonVisibility, setButtonVisibility] = useState({
    cart: true,
    restock: true,
    return: true,
    quantity: true,
    actions: true,
    inventory: true,
    loss: true,
    delete: true,
    add: true,
  });

  useEffect(() => {
    fetchAccessControls();
  }, []);

  const fetchAccessControls = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('access_controls')
        .select('*')
        .eq('owner_id', user.id)
        .eq('is_disabled', false);

      if (error) throw error;

      const newVisibility = { ...buttonVisibility };
      
      if (data) {
        data.forEach(control => {
          const buttonId = control.feature_name.replace('button_', '');
          if (buttonId in newVisibility) {
            newVisibility[buttonId as keyof typeof buttonVisibility] = !control.is_disabled;
          }
        });
      }

      setButtonVisibility(newVisibility);
    } catch (error) {
      console.error("Error fetching access controls:", error);
    }
  };

  const handleToggleVisibility = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const featureName = `button_${id}`;
      
      const { data: existingControl } = await supabase
        .from('access_controls')
        .select('*')
        .eq('owner_id', user.id)
        .eq('feature_name', featureName)
        .maybeSingle();

      if (existingControl) {
        const { error } = await supabase
          .from('access_controls')
          .update({
            is_disabled: !existingControl.is_disabled,
            last_verified_at: new Date().toISOString(),
          })
          .eq('id', existingControl.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('access_controls')
          .insert([
            {
              owner_id: user.id,
              feature_name: featureName,
              access_code: '',
              is_disabled: true,
              visibility_duration_minutes: 17,
              last_verified_at: new Date().toISOString(),
            }
          ]);

        if (error) throw error;
      }

      setButtonVisibility(prev => ({
        ...prev,
        [id]: !prev[id as keyof typeof buttonVisibility]
      }));

    } catch (error) {
      console.error("Error toggling visibility:", error);
      throw error;
    }
  };

  return {
    buttonVisibility,
    handleToggleVisibility,
  };
};